import { useSeoMeta } from '@unhead/react';
import { useEffect, useRef, useState } from 'react';
import {
  ExternalLink,
  Menu,
  X,
  Mail,
  Shield,
  Lock,
  Eye,
  Server,
  Zap,
  Key,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SiteFooter } from '@/components/SiteFooter';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */
const GithubIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Nori SVG Mascot                                                    */
/* ------------------------------------------------------------------ */
const NoriLogo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <svg
    viewBox="0 0 20 16"
    shapeRendering="crispEdges"
    aria-label="Nori the ostrich"
    className={className}
  >
    <g fill="#7B68EE">
      <rect x="8" y="0" width="2" height="1"/>
      <rect x="7" y="1" width="1" height="2"/>
      <rect x="10" y="1" width="1" height="2"/>
      <rect x="6" y="3" width="2" height="1"/>
      <rect x="10" y="3" width="2" height="1"/>
      <rect x="4" y="4" width="10" height="1"/>
      <rect x="3" y="5" width="1" height="3"/>
      <rect x="14" y="5" width="1" height="3"/>
      <rect x="7" y="6" width="2" height="1"/>
      <rect x="4" y="8" width="2" height="1"/>
      <rect x="12" y="8" width="2" height="1"/>
      <rect x="6" y="9" width="6" height="1"/>
      <rect x="6" y="10" width="1" height="1"/>
      <rect x="11" y="10" width="1" height="1"/>
      <rect x="5" y="11" width="2" height="1"/>
      <rect x="11" y="11" width="2" height="1"/>
      <rect x="5" y="12" width="1" height="2"/>
      <rect x="12" y="12" width="1" height="2"/>
      <rect x="4" y="14" width="3" height="1"/>
      <rect x="9" y="14" width="4" height="1"/>
      <rect x="3" y="15" width="1" height="1"/>
      <rect x="6" y="15" width="1" height="1"/>
      <rect x="9" y="15" width="1" height="1"/>
      <rect x="12" y="15" width="1" height="1"/>
    </g>
    <g fill="#C4BBFF">
      <rect x="8" y="1" width="2" height="2"/>
      <rect x="8" y="3" width="2" height="1"/>
      <rect x="4" y="5" width="10" height="1"/>
      <rect x="4" y="6" width="3" height="1"/>
      <rect x="9" y="6" width="5" height="1"/>
      <rect x="4" y="7" width="10" height="1"/>
      <rect x="6" y="8" width="6" height="1"/>
      <rect x="7" y="10" width="4" height="1"/>
      <rect x="7" y="11" width="4" height="1"/>
      <rect x="6" y="12" width="6" height="1"/>
      <rect x="6" y="13" width="1" height="1"/>
      <rect x="9" y="13" width="3" height="1"/>
    </g>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Navigation                                                         */
/* ------------------------------------------------------------------ */
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'About', href: '/' },
    { label: 'Stack', href: '/stack' },
    { label: 'Get Started', href: '/get-started' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-background/80 backdrop-blur-xl border-b border-border/50'
          : 'bg-transparent'
      }`}
    >
      <div className="container grid grid-cols-[1fr_1fr] lg:grid-cols-[1fr_auto_1fr] items-center h-16">
        <div className="flex items-center">
          <a href="/" className="flex items-center gap-2.5 group shrink-0">
            <NoriLogo className="w-7 h-7 transition-transform group-hover:scale-110 shrink-0" />
            <span className="font-semibold text-lg tracking-tight whitespace-nowrap">
              nostr-station
            </span>
          </a>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center justify-end gap-3">
          <a
            href="https://github.com/jared-logan/nostr-station"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex"
          >
            <button className="inline-flex items-center justify-center rounded-full h-9 w-9 hover:bg-accent hover:text-accent-foreground transition-colors">
              <GithubIcon className="w-5 h-5" />
            </button>
          </a>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden inline-flex items-center justify-center rounded-full h-9 w-9 hover:bg-accent hover:text-accent-foreground transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden border-t border-border/50">
          <nav className="container py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-accent/50"
              >
                {link.label}
              </a>
            ))}
            <a
              href="https://github.com/jared-logan/nostr-station"
              target="_blank"
              rel="noopener noreferrer"
              className="sm:hidden text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-accent/50 flex items-center gap-2"
            >
              <GithubIcon className="w-4 h-4" />
              GitHub
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

/* ------------------------------------------------------------------ */
/*  Hero Section                                                       */
/* ------------------------------------------------------------------ */
const HeroSection = () => {
  return (
    <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-16">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,hsl(265_85%_60%/0.15),transparent)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_600px_at_50%_50%,hsl(265_70%_55%/0.08),transparent)]" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(hsl(265 85% 60%) 1px, transparent 1px), linear-gradient(90deg, hsl(265 85% 60%) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />

      <div className="container relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow */}
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            Encrypted Relay Mail
          </span>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
            Email, <span className="text-gradient">reimagined</span>
          </h1>

          {/* Description */}
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Encrypted email over Nostr. NIP-17 gift-wrap hides sender and recipient metadata, RFC-2822 wire format inside sealed DMs, Blossom attachments — mail that lives on relays, not servers.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Features Section                                                   */
/* ------------------------------------------------------------------ */
const features = [
  {
    icon: Lock,
    title: 'NIP-17 gift-wrap',
    description: 'Every message is sealed in a gift-wrapped event (kind 1059) that hides both sender and recipient metadata from relay operators. Only the intended recipient can unwrap and read.',
    color: 'hsl(265 85% 60%)',
  },
  {
    icon: Mail,
    title: 'RFC-2822 compatible',
    description: 'Full email semantics inside Nostr events. Subject lines, CC, BCC, threaded replies, and multipart MIME — all carried in the RFC-2822 wire format embedded in sealed DMs.',
    color: 'hsl(280 85% 60%)',
  },
  {
    icon: Server,
    title: 'Blossom attachments',
    description: 'Large files are uploaded to Blossom blob servers and referenced by SHA256 hash in the mail event. No base64 bloat in relay events. Attachments are content-addressed and verifiable.',
    color: 'hsl(295 85% 60%)',
  },
  {
    icon: Eye,
    title: 'No SMTP, no servers',
    description: 'Traditional email requires trusting SMTP servers, MX records, and hosting providers. nostr-mail needs none of these. Your mail lives on relays you choose — or your own.',
    color: 'hsl(250 85% 60%)',
  },
  {
    icon: Zap,
    title: 'Inbox routing (kind 10050)',
    description: 'Publish your preferred inbox relays so senders know where to deliver. Rotate relays anytime without changing your address. Your NIP-05 identifier stays constant.',
    color: 'hsl(290 85% 60%)',
  },
  {
    icon: Shield,
    title: 'Label-organized (NIP-32)',
    description: 'Organize mail with labels and folders using NIP-32 label events (kind 1985). Inbox, sent, archive, custom folders — all as user-controlled metadata on relays.',
    color: 'hsl(260 85% 60%)',
  },
];

const FeaturesSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            Features
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Mail that respects <span className="text-gradient">privacy</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className={`group relative p-6 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 hover:glow-purple ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                style={{ backgroundColor: `${feature.color}15` }}
              >
                <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
              </div>
              <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  How It Works Section                                               */
/* ------------------------------------------------------------------ */
const HowItWorksSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      icon: Key,
      title: 'Compose',
      description: 'Write email as usual in the dashboard or any NIP-17-compatible client. Add attachments up to your Blossom server\'s limit.',
    },
    {
      icon: Lock,
      title: 'Seal & wrap',
      description: 'The message is encrypted with NIP-44, sealed in a kind 14 DM, then gift-wrapped in kind 1059. Relay operators see only opaque blobs.',
    },
    {
      icon: Server,
      title: 'Deliver to inbox relays',
      description: 'The sender looks up your kind 10050 inbox relays and publishes the gift-wrap there. You decrypt and read on any device with your nsec.',
    },
  ];

  return (
    <section id="how-it-works" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,hsl(265_85%_60%/0.06),transparent)]" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            How it works
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Three steps to <span className="text-gradient">encrypted mail</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {steps.map((step, index) => (
            <div
              key={step.title}
              className={`group relative p-6 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 hover:glow-purple ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                <step.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Comparison Section                                                 */
/* ------------------------------------------------------------------ */
const ComparisonSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const rows = [
    { axis: 'Metadata privacy', traditional: 'SMTP servers see sender, recipient, subject, timestamps', nostrMail: 'Gift-wrap hides all metadata from relays; only recipient can unwrap' },
    { axis: 'Hosting required', traditional: 'Yes — mailbox provider or self-hosted server', nostrMail: 'No — mail lives on any Nostr relay' },
    { axis: 'Spam filtering', traditional: 'Centralized blacklists and provider algorithms', nostrMail: 'Whitelist-by-default; only decrypt mail from trusted pubkeys' },
    { axis: 'Attachment storage', traditional: 'Provider-controlled quotas and scanning', nostrMail: 'Blossom blobs on servers you choose; SHA256-verified' },
    { axis: 'Censorship resistance', traditional: 'Provider can block, filter, or delete mail', nostrMail: 'Relay-agnostic; publish to multiple relays for redundancy' },
    { axis: 'Identity', traditional: 'DNS-dependent email address, registrar-required', nostrMail: 'NIP-05 or raw npub; no registrar, no DNS, no hosting' },
  ];

  return (
    <section id="comparison" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            Comparison
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            vs. traditional <span className="text-gradient">email</span>
          </h2>
        </div>

        <div className={`max-w-4xl mx-auto transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs">
                      Axis
                    </th>
                    <th className="text-left px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs min-w-[240px]">
                      Traditional Email
                    </th>
                    <th className="text-left px-6 py-4 font-medium text-primary uppercase tracking-wider text-xs min-w-[240px]">
                      nostr-mail
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      key={row.axis}
                      className={`border-b border-border/30 hover:bg-primary/5 transition-colors ${index === rows.length - 1 ? 'border-b-0' : ''}`}
                    >
                      <td className="px-6 py-4 font-medium text-foreground whitespace-nowrap">
                        {row.axis}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {row.traditional}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {row.nostrMail}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  CTA Section                                                        */
/* ------------------------------------------------------------------ */
const CtaSection = () => {
  return (
    <section className="py-24 sm:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,hsl(265_85%_60%/0.08),transparent)]" />

      <div className="container relative text-center">
        <Sparkles className="w-12 h-12 text-primary mx-auto mb-6" />
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6">
          Install nostr-station to try <span className="text-gradient">nostr-mail</span>
        </h2>
        <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
          One command gets you a complete local Nostr stack with encrypted relay-mail ready to send.
        </p>
        <a href="/get-started">
          <Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-12 text-base">
            Get Started
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </a>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */
const NostrMail = () => {
  useSeoMeta({
    title: 'nostr-mail — Encrypted Relay Mail — nostr-station',
    description: 'Encrypted email over Nostr. NIP-17 gift-wrap hides sender and recipient metadata, RFC-2822 wire format inside sealed DMs, Blossom attachments — no SMTP.',
    ogTitle: 'nostr-mail — Encrypted Relay Mail — nostr-station',
    ogDescription: 'Encrypted email over Nostr. NIP-17 gift-wrap hides sender and recipient metadata, RFC-2822 wire format inside sealed DMs, Blossom attachments — no SMTP.',
    twitterCard: 'summary_large_image',
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      <HeroSection />
      <FeaturesSection />
      <HowItWorksSection />
      <ComparisonSection />
      <CtaSection />
      <SiteFooter />
    </div>
  );
};

export default NostrMail;
